import type { AgentInputItem } from '../types';
import type { LocalCompactionConfig } from './types';

const DEFAULT_CONTEXT_WINDOW_TOKENS = 128_000;
const DEFAULT_THRESHOLD_RATIO = 0.85;
const DEFAULT_RESERVE_OUTPUT_TOKENS = 20_000;
const BINARY_TOKEN_COST = 1_000;

export function estimateTextTokens(text: string): number {
  return Math.max(1, Math.ceil(text.length / 4));
}

export function estimateTokensForItems(
  items: AgentInputItem[],
  options: { instructions?: string } = {},
): number {
  let total = options.instructions
    ? estimateTextTokens(options.instructions)
    : 0;
  for (const item of items) {
    total += estimateTokensForItem(item);
  }
  return total;
}

export function estimateTokensForItem(item: AgentInputItem): number {
  return estimateValueTokens(stripLocalItemIdentity(item));
}

export function calculateCompactionThreshold(
  config: LocalCompactionConfig = {},
): number {
  if (typeof config.thresholdTokens === 'number') {
    return Math.max(1, Math.floor(config.thresholdTokens));
  }
  const contextWindow = Math.max(
    1,
    Math.floor(config.contextWindowTokens ?? DEFAULT_CONTEXT_WINDOW_TOKENS),
  );
  const reserve = Math.max(
    0,
    Math.floor(config.reserveOutputTokens ?? DEFAULT_RESERVE_OUTPUT_TOKENS),
  );
  const effectiveWindow = Math.max(1, contextWindow - reserve);
  const ratio = config.thresholdRatio ?? DEFAULT_THRESHOLD_RATIO;
  return Math.max(1, Math.floor(effectiveWindow * ratio));
}

function estimateValueTokens(value: unknown): number {
  if (value === null || typeof value === 'undefined') {
    return 1;
  }
  if (typeof value === 'string') {
    return estimateTextTokens(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return 1;
  }
  if (value instanceof Uint8Array || value instanceof ArrayBuffer) {
    return BINARY_TOKEN_COST;
  }
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + estimateValueTokens(item), 0);
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const imageTokens = estimateInputImageTokens(record);
    if (imageTokens !== undefined) {
      return imageTokens;
    }
    if (
      typeof record.data === 'string' &&
      record.data.length > 512 &&
      typeof record.mediaType === 'string'
    ) {
      return BINARY_TOKEN_COST;
    }
    return estimateObjectTokens(record);
  }
  return estimateTextTokens(String(value));
}

function estimateObjectTokens(record: Record<string, unknown>): number {
  let total = 2;
  for (const [key, value] of Object.entries(record)) {
    total += estimateTextTokens(key);
    total += estimateValueTokens(value);
  }
  return total;
}

function estimateInputImageTokens(
  record: Record<string, unknown>,
): number | undefined {
  if (record.type !== 'input_image') {
    return undefined;
  }
  const providerData =
    record.providerData && typeof record.providerData === 'object'
      ? (record.providerData as Record<string, unknown>)
      : undefined;
  const explicit = providerData?.estimatedImageTokens;
  if (typeof explicit === 'number' && Number.isFinite(explicit)) {
    return Math.max(1, Math.ceil(explicit));
  }
  const pixelCount = providerData?.pixelCount;
  if (typeof pixelCount === 'number' && Number.isFinite(pixelCount)) {
    return estimateImageTokensForPixels(pixelCount);
  }
  return 8_000;
}

function estimateImageTokensForPixels(pixelCount: number): number {
  if (pixelCount <= 1_000_000) {
    return 1_000;
  }
  if (pixelCount <= 4_000_000) {
    return 2_000;
  }
  if (pixelCount <= 16_000_000) {
    return 4_000;
  }
  return 8_000;
}

function stripLocalItemIdentity(item: AgentInputItem): AgentInputItem {
  if (!item || typeof item !== 'object') {
    return item;
  }
  const { id: _id, providerData, ...rest } = item as Record<string, unknown>;
  if (!providerData || typeof providerData !== 'object') {
    return rest as AgentInputItem;
  }
  const { providerItemId: _providerItemId, ...remainingProviderData } =
    providerData as Record<string, unknown>;
  if (Object.keys(remainingProviderData).length === 0) {
    return rest as AgentInputItem;
  }
  return {
    ...rest,
    providerData: remainingProviderData,
  } as AgentInputItem;
}
